import { useQuery } from "@tanstack/react-query";
import {
  type FunctionComponent,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createAnnouncementMetaQueryOptions } from "@/features/announce/hooks/announcementMetaQuery";
import {
  type AnnouncementStatus,
  AnnouncementStatusContext,
} from "@/features/announce/hooks/useAnnouncementStatus";
import {
  applyAnnouncementReadRevisionStorageEvent,
  applyAnnouncementReadStorageEvent,
  persistAnnouncementReadRevision,
  persistAnnouncementReadTimestamp,
  readAnnouncementReadRevision,
  readAnnouncementReadTimestamp,
} from "@/features/announce/lib/announcementReadState";
import { isAnnouncementEvent } from "@/features/announce/mediators/announcementEvents";
import {
  type AnnouncementStatusEffect,
  createAnnouncementStatusMediator,
  isAnnouncementStatusEvent,
} from "@/features/announce/mediators/announcementStatusMediator";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";

/** PC/SP共通のmeta queryと既読状態をアプリ枠の外側に1つだけ保持する。 */
export const AnnouncementStatusProvider: FunctionComponent<
  PropsWithChildren
> = ({ children }: PropsWithChildren) => {
  const metaQuery = useQuery(createAnnouncementMetaQueryOptions());
  const canReserveMeta =
    !metaQuery.isFetched &&
    metaQuery.isPending &&
    metaQuery.fetchStatus !== "paused";
  const [mediator] = useState(() => {
    const initialMediator = createAnnouncementStatusMediator(
      {
        canReserveMeta,
        readRevision: readAnnouncementReadRevision(),
        readTimestamp: readAnnouncementReadTimestamp(),
      },
      runStatusEffect,
    );
    initialMediator.dispatch({
      type: "announcement/status-meta-synced",
      articleTimestamp: metaQuery.data?.article ?? null,
      articleUnreadRevision: metaQuery.data?.article_unread_revision ?? 0,
      isMetaReady: metaQuery.isSuccess,
      canReserveMeta,
    });
    return initialMediator;
  });
  useHierarchyLifetime(mediator);
  const state = useMediatorSnapshot(mediator);
  const handler = useCallback(
    (event: { readonly type: string }): "handled" | "unhandled" => {
      if (!isAnnouncementEvent(event)) return "unhandled";
      if (!isAnnouncementStatusEvent(event)) return "unhandled";
      const result = mediator.dispatch(event);
      return result.status === "handled" ? "handled" : "unhandled";
    },
    [mediator],
  );
  const node = useComponentNode(handler);

  useEffect(() => {
    mediator.dispatch({
      type: "announcement/status-meta-synced",
      articleTimestamp: metaQuery.data?.article ?? null,
      articleUnreadRevision: metaQuery.data?.article_unread_revision ?? 0,
      isMetaReady: metaQuery.isSuccess,
      canReserveMeta,
    });
  }, [
    canReserveMeta,
    mediator,
    metaQuery.data?.article,
    metaQuery.data?.article_unread_revision,
    metaQuery.isSuccess,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleStorage = (event: StorageEvent): void => {
      const current = mediator.getSnapshot();
      mediator.dispatch({
        type: "announcement/status-storage-synced",
        readTimestamp: applyAnnouncementReadStorageEvent(
          current.readTimestamp,
          event,
        ),
        readRevision: applyAnnouncementReadRevisionStorageEvent(
          current.readRevision,
          event,
        ),
      });
    };
    window.addEventListener("storage", handleStorage);
    return (): void => window.removeEventListener("storage", handleStorage);
  }, [mediator]);

  const dispatch = useCallback(
    (event: Parameters<typeof mediator.dispatch>[0]): void => {
      node.dispatch(event);
    },
    [node],
  );
  const markAnnouncementRead = useCallback<
    AnnouncementStatus["markAnnouncementRead"]
  >(
    (announcement) =>
      dispatch({ type: "announcement/read-requested", announcement }),
    [dispatch],
  );
  const markAnnouncementsReadThrough = useCallback<
    AnnouncementStatus["markAnnouncementsReadThrough"]
  >(
    (publishedAt, articleUnreadRevision = null) =>
      dispatch({
        type: "announcement/read-through-requested",
        publishedAt,
        articleUnreadRevision,
      }),
    [dispatch],
  );
  const markCurrentAnnouncementsRead = useCallback<
    AnnouncementStatus["markCurrentAnnouncementsRead"]
  >(
    (announcements, snapshotRevision = null) =>
      dispatch({
        type: "announcement/current-list-read-requested",
        announcements,
        snapshotRevision,
      }),
    [dispatch],
  );
  const value = useMemo<AnnouncementStatus>(
    () => ({
      hasUnreadAnnouncements: mediator.hasUnreadAnnouncements(),
      isAnnouncementMetaReady: state.isMetaReady,
      isAnnouncementMetaPending: mediator.isAnnouncementMetaPending(),
      isAnnouncementRead: mediator.isAnnouncementRead,
      markAnnouncementRead,
      markAnnouncementsReadThrough,
      markCurrentAnnouncementsRead,
    }),
    [
      markAnnouncementRead,
      markAnnouncementsReadThrough,
      markCurrentAnnouncementsRead,
      mediator,
      state,
    ],
  );

  return (
    <AnnouncementStatusContext.Provider value={value}>
      <ComponentScope node={node}>{children}</ComponentScope>
    </AnnouncementStatusContext.Provider>
  );
};

function runStatusEffect(effect: AnnouncementStatusEffect): void {
  if (effect.type === "persist-read-timestamp") {
    persistAnnouncementReadTimestamp(effect.value);
    return;
  }
  persistAnnouncementReadRevision(effect.value);
}
